import { createConfigurationError } from "./errors.ts";
import type { FormbrewClientOptions } from "./types.ts";

export type ResolvedClientConfiguration = {
  baseUrl: string;
  fetchImplementation: typeof globalThis.fetch;
  origin?: string;
  token: string;
};

const defaultBaseUrl = "https://api.formbrew.com/v1";

export const resolveClientConfiguration = (
  options: FormbrewClientOptions,
): ResolvedClientConfiguration => {
  const token = normalizeToken(options.token);
  const baseUrl = normalizeBaseUrl(options.baseUrl ?? defaultBaseUrl);
  const fetchImplementation = options.fetch ?? globalThis.fetch;
  let origin: string | undefined;

  if (options.origin !== undefined) {
    origin = normalizeOrigin(options.origin);
  }

  if (typeof fetchImplementation !== "function") {
    throw createConfigurationError("A compatible fetch implementation is required.");
  }

  return { baseUrl, fetchImplementation, origin, token };
};

const normalizeToken = (value: string): string => {
  const token = value.trim();

  if (!token) {
    throw createConfigurationError("A public form token is required.");
  }

  return token;
};

const normalizeBaseUrl = (value: string): string => {
  const url = parseHttpUrl(value, "The Formbrew base URL must be an absolute HTTP(S) URL.");

  if (url.username || url.password || url.search || url.hash) {
    throw createConfigurationError(
      "The Formbrew base URL cannot contain credentials, a query, or a hash.",
    );
  }

  return `${url.origin}${url.pathname.replace(/\/+$/, "")}`;
};

const normalizeOrigin = (value: string): string => {
  const url = parseHttpUrl(value, "Origin must be an absolute HTTP(S) origin.");

  if (url.username || url.password) {
    throw createConfigurationError("Origin cannot contain credentials.");
  }

  return url.origin;
};

const parseHttpUrl = (value: string, message: string): URL => {
  let url: URL;

  try {
    url = new URL(value);
  } catch {
    throw createConfigurationError(message);
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw createConfigurationError(message);
  }

  return url;
};
